import { Link } from 'react-router-dom';
import { useDashboardData } from '@/hooks/useDashboardData';
import KpiCards from './components/KpiCards';
import TodayLessons from './components/TodayLessons';
import UpcomingWeek from './components/UpcomingWeek';
import RecentRecords from './components/RecentRecords';
import PendingAssignments from './components/PendingAssignments';

function SectionHeader({ title, icon, link }: { title: string; icon: string; link?: string }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
          <i className={`${icon} text-base`} />
        </span>
        <h3 className="font-heading text-lg font-bold text-foreground-950">{title}</h3>
      </div>
      {link && (
        <Link
          to={link}
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 transition hover:text-primary-700 cursor-pointer whitespace-nowrap"
        >
          Tümünü Gör <i className="ri-arrow-right-line text-sm" />
        </Link>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { groups, lessons, records, assignments, documents, activeStudents, passiveStudents, loading, error } = useDashboardData();

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Dashboard yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Veriler yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Merhaba Zehra Hoca 👋</h1>
        <p className="mt-1 text-sm text-foreground-600">İşte bugünkü ders ve takip özetiniz.</p>
      </div>

      <KpiCards groups={groups} lessons={lessons} records={records} assignments={assignments} activeStudents={activeStudents} passiveStudents={passiveStudents} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <SectionHeader title="Bugünün Dersleri" icon="ri-calendar-check-line" link="/panel/program" />
          <TodayLessons lessons={lessons} groups={groups} />
        </div>
        <div>
          <SectionHeader title="Yaklaşan Dersler" icon="ri-calendar-2-line" link="/panel/program" />
          <UpcomingWeek lessons={lessons} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div>
          <SectionHeader title="Son Yüklenen Ders Kayıtları" icon="ri-vidicon-line" link="/panel/kayitlar" />
          <RecentRecords records={records} lessons={lessons} groups={groups} />
        </div>
        <div>
          <SectionHeader title="Bekleyen Ödev Kontrolleri" icon="ri-task-line" link="/panel/odevler" />
          <PendingAssignments assignments={assignments} groups={groups} />
        </div>
      </div>
    </div>
  );
}